import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { storeService } from '../services/storeService';
import { StoreCatalogItem } from '../models';
import { ProductCard } from '../components/ui/ProductCard';
import { Spinner } from '../components/ui/Spinner';
import { EmptyState } from '../components/ui/EmptyState';
import { Pagination } from '../components/ui/Pagination';
import { Button } from '../components/ui/Button';
import { useScrollToTopOnChange } from '../hooks/useScrollToTopOnChange';

const PAGE_SIZE = 16;

export function FavoritesPage() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const navigate = useNavigate();

  const [items, setItems] = useState<StoreCatalogItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  const fetchFavorites = useCallback(async (pageNumber: number) => {
    setLoading(true);
    try {
      const data = await storeService.getFavorites({ pageNumber, pageSize: PAGE_SIZE });
      setItems(data.items);
      setTotal(data.count);
    } catch {
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchFavorites(page);
  }, [isAuthenticated, page, fetchFavorites]);

  useScrollToTopOnChange(page);

  if (!isAuthenticated) return null;

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="max-w-7xl mx-auto px-4 py-10">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-display font-semibold text-text-dark">Meus Favoritos</h1>
        <p className="text-sm text-text-light mt-1">
          {total} {total === 1 ? 'produto favoritado' : 'produtos favoritados'}
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <Spinner size="lg" />
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          title="Você ainda não tem favoritos"
          description="Explore o catálogo e toque no coração dos produtos que você mais gostar."
          action={<Link to="/catalogo"><Button>Ver catálogo</Button></Link>}
        />
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {items.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>

          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        </>
      )}
    </div>
  );
}
